import { InvoiceDocumentType } from '~/app/invoicing/enums/InvoiceDocumentType';
import { EINVOICING_SENDING_STATUS } from '~/app/einvoicing/enums/EinvoicingSendingStatus';

/**
 * Why a document opened read-only, as far as the frontend can tell.
 *
 * The backend only says *that* a document is locked (`meta.isEditable`), never why, so
 * the reason is read off the relations the document form already loads. Each branch
 * names a cause only when that relation is actually there; anything else falls through
 * to `other`, which the warning words generically. A wrong reason is worse than a vague
 * one: it sends the user looking for a progress billing that does not exist.
 */
export type DocumentEditLockReason = 'progress_billing' | 'einvoicing' | 'other';

/** The slice of `Invoice` this reads, so it can be tested without the model. */
export interface LockableDocument {
    isEditable: boolean;
    documentType: string;
    worksiteProgressBillings?: { isNotEmpty(): boolean } | null;
    currentEinvoicingSending?: { status: string } | null;
}

// Pending counts as sent: the document is in flight to the recipient, and editing it now
// would change what they are about to receive. Failed and no-action sendings do not name
// the lock: the backend may have locked the document for another reason entirely.
const EINVOICING_LOCKING_STATUSES: string[] = [EINVOICING_SENDING_STATUS.SUCCEEDED, EINVOICING_SENDING_STATUS.PENDING];

/** `null` when the document can be edited, i.e. when there is nothing to warn about. */
export function documentEditLockReason(document: LockableDocument | null | undefined): DocumentEditLockReason | null {
    if (!document || document.isEditable) return null;

    if (document.documentType === InvoiceDocumentType.QUOTE && document.worksiteProgressBillings?.isNotEmpty()) {
        return 'progress_billing';
    }

    const sentThroughEinvoicing = [InvoiceDocumentType.INVOICE, InvoiceDocumentType.CREDIT_NOTE].includes(document.documentType as never);
    if (sentThroughEinvoicing && EINVOICING_LOCKING_STATUSES.includes(document.currentEinvoicingSending?.status ?? '')) {
        return 'einvoicing';
    }

    return 'other';
}

/**
 * The title the "not editable" warning shows for each reason.
 *
 * `progress_billing` keeps the key the warning always had: it reads "le document est lié
 * à un état d'avancements", which is exactly that reason, so a locked quote renders as
 * before on desktop.
 */
export const DOCUMENT_EDIT_LOCK_TITLES: Record<DocumentEditLockReason, string> = {
    progress_billing: 'invoicing.invoice_form.edit_document_alert.title',
    einvoicing: 'invoicing.invoice_form.edit_document_alert.einvoicing',
    other: 'invoicing.invoice_form.edit_document_alert.locked',
};
